import React from 'react';
import { Text, Title } from '@dropbox/dig-components/typography';
import { Button } from '@dropbox/dig-components/buttons';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';
import styles from './index.scss';
import { getPlanTestId, toPricingTestIdSegment } from './helpers';
import type { BillingPlan } from '../types';

const messages = defineMessages({
  purchaseNow: {
    id: 'pricing.purchaseNowLink',
    description: 'Link text to purchase a plan immediately without a trial',
    defaultMessage: 'or <a>purchase now</a>',
  },
});

export interface PricingPlanHeaderProps {
  plan: BillingPlan;
  planIndex: number;
  columnSlot?: 'left' | 'right';
  isCompact?: boolean;
  rateText: React.ReactNode;
  buttonHref?: string;
  buttonLabel: string;
  buttonAction:
    | 'current-plan'
    | 'downgrade'
    | 'get-started'
    | 'start-free-trial'
    | 'upgrade';
  buttonDisabled?: boolean;
  directPurchaseHref?: string;
  showDirectPurchaseLink?: boolean;
}

function getPlanSubtitle(plan: BillingPlan): string | null {
  if (plan.name && plan.name !== plan.tier) {
    return plan.name;
  }
  return null;
}

const PricingPlanHeader: React.FC<PricingPlanHeaderProps> = ({
  plan,
  planIndex,
  columnSlot,
  isCompact = false,
  rateText,
  buttonHref,
  buttonLabel,
  buttonAction,
  buttonDisabled = false,
  directPurchaseHref,
  showDirectPurchaseLink = false,
}) => {
  const intl = useIntl();
  const subtitle = getPlanSubtitle(plan);
  const planSegment = toPricingTestIdSegment(plan.tier || plan.plan_id);

  const titleBlock = (
    <div
      className={styles.planHeaderTitleBlock}
      data-testid="pricing-plan-name"
    >
      <Title
        weightVariant="normal"
        size="medium"
        className={styles.planHeaderTitle}
      >
        {plan.tier}
      </Title>
      {isCompact && subtitle && (
        <Text
          variant="paragraph"
          size="small"
          tagName="p"
          color="subtle"
          className={styles.planHeaderSubtitle}
        >
          {subtitle}
        </Text>
      )}
    </div>
  );

  const pricingBlock = (
    <div className={styles.planHeaderPricing} data-testid="pricing-plan-price">
      {rateText}
    </div>
  );

  const ctaButton = (
    <Button
      className={styles.planHeaderButton}
      variant="primary"
      size="medium"
      href={buttonDisabled ? undefined : buttonHref}
      disabled={buttonDisabled}
      data-testid="pricing-plan-cta"
      data-pricing-plan={planSegment}
      data-pricing-cta={buttonAction}
    >
      {buttonLabel}
    </Button>
  );

  return (
    <div
      className={classNames(styles.planHeader, {
        [styles.planHeaderCompact]: isCompact,
      })}
      data-plan-index={planIndex}
      data-column-slot={columnSlot}
      data-testid={getPlanTestId(plan, columnSlot)}
      aria-label={plan.tier}
    >
      {titleBlock}
      {pricingBlock}
      <div className={styles.planHeaderButtonRow}>{ctaButton}</div>
      <div className={styles.planHeaderDirectPurchaseSlot}>
        {showDirectPurchaseLink && directPurchaseHref && (
          <Text
            variant="paragraph"
            size="small"
            tagName="p"
            color="subtle"
            className={styles.planHeaderDirectPurchase}
          >
            {intl.formatMessage(messages.purchaseNow, {
              a: (chunks: React.ReactNode) => (
                <a
                  href={directPurchaseHref}
                  data-testid="pricing-plan-direct-purchase-link"
                >
                  {chunks}
                </a>
              ),
            })}
          </Text>
        )}
      </div>
    </div>
  );
};

export default PricingPlanHeader;
